/** Pure helpers for the coverage table: what is still a person logging into a portal each month. */

export const AUTOMATION_LABELS = {
  Automated: 'Supported',
  ScraperIdle: 'Supported, unused',
  Manual: 'Not supported',
  Dormant: 'Not in use'
};

/** MUI Chip colours. Not supported is work, not an error. */
export const AUTOMATION_COLORS = {
  Automated: 'success',
  ScraperIdle: 'info',
  Manual: 'warning',
  Dormant: 'default'
};

/**
 * The four quadrants as counters, plus the two a reader acts on: `manualUtilities` (fetched by hand
 * each cycle) and `needsAttention` (failed credentials, so automation that became manual).
 */
export const coverageSummary = function () {
  let rows = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  const list = Array.isArray(rows) ? rows : [];
  const by = state => list.filter(r => r.automation === state);
  return {
    total: list.length,
    automated: by('Automated').length,
    manual: by('Manual').length,
    scraperIdle: by('ScraperIdle').length,
    dormant: by('Dormant').length,
    // Providers with utilities actually routed to them. The bare row count is dominated by catalog
    // entries no property uses, which makes the working set look far larger than it is.
    inFlight: by('Automated').length + by('Manual').length,
    // Scrapers that exist, whether or not anything is routed to them yet. Built-but-unrouted is a
    // demand problem, not an absence of work, and counting only Automated hides it.
    built: by('Automated').length + by('ScraperIdle').length,
    supported: by('Automated').length + by('ScraperIdle').length,
    // A row that only shares a scraper's name routes nowhere, so its utilities are still fetched by hand.
    manualUtilities: list.reduce((sum, r) => sum + (r.automation === 'Manual' ? r.utilities || 0 : r.unmappedUtilities || 0), 0),
    automatedUtilities: by('Automated').reduce((sum, r) => sum + (r.utilities || 0) - (r.unmappedUtilities || 0), 0),
    needsAttention: list.reduce((sum, r) => sum + (r.credentialsNeedingAttention || 0), 0),
    actionRequired: list.filter(r => r.actionRequired).length
  };
};

/**
 * Share of in-flight utilities a scraper covers.
 *
 * Deliberately weighted by utilities rather than by provider count: 40 providers automated means
 * nothing if the two manual ones carry most of the portfolio.
 */
export const automationPercent = function () {
  let rows = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  const summary = coverageSummary(rows);
  const total = summary.manualUtilities + summary.automatedUtilities;
  return total ? Math.round(summary.automatedUtilities / total * 100) : 0;
};
export const filterCoverage = function () {
  let rows = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  let _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref$automation = _ref.automation,
    automation = _ref$automation === void 0 ? 'all' : _ref$automation,
    _ref$search = _ref.search,
    search = _ref$search === void 0 ? '' : _ref$search,
    _ref$onlyActionRequir = _ref.onlyActionRequired,
    onlyActionRequired = _ref$onlyActionRequir === void 0 ? false : _ref$onlyActionRequir,
    _ref$onlyWithCredenti = _ref.onlyWithCredentials,
    onlyWithCredentials = _ref$onlyWithCredenti === void 0 ? false : _ref$onlyWithCredenti;
  const term = (search || '').trim().toLowerCase();
  return (Array.isArray(rows) ? rows : []).filter(row => {
    if (automation !== 'all' && row.automation !== automation) return false;
    if (onlyActionRequired && !row.actionRequired && !row.credentialsNeedingAttention) return false;
    if (onlyWithCredentials && !row.credentialsStored) return false;
    if (!term) return true;
    // Member names too, so a company's own spelling finds the provider it was folded into.
    return [row.name, row.scraperProviderKey, ...(row.members || []).map(m => m.name)].some(value => (value || '').toLowerCase().includes(term));
  });
};

/**
 * A stable row id for the grid.
 *
 * A registry-only row has no provider id, and a provider with no scraper key has no registry key,
 * so neither alone is unique across the full outer join.
 */
export const coverageRowId = row => row.utilityProviderId || "registry:".concat(row.scraperProviderKey);